import { Link, Navigate } from 'react-router-dom';
import { ArrowRight, ScanLine, Smartphone } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import { homeForRole } from '@/lib/roles';
import { cn } from '@/lib/cn';
import { Logo } from '@/components/ui/Logo';

/*
  The front door.

  SafePass is three audiences on one deployment, and nobody arriving here has
  been told which URL is theirs. So the first screen asks, in their words —
  "I'm a parent" / "I'm on the gate" — rather than naming apps or roles they
  would have to decode.

  If they are already signed in, this screen is skipped entirely: the role on
  their token already says where they belong.
*/

const CHOICES = [
  {
    to: '/parent/login',
    label: "I'm a parent or guardian",
    detail: "See today's pickup code for your child, authorise someone else to collect them, and check pickup history.",
    action: 'Sign in with your phone number',
    icon: Smartphone,
  },
  {
    to: '/login?as=gate',
    label: "I'm on the gate",
    detail: 'Verify a pickup code, check who is authorised to collect the child, and release them.',
    action: 'Sign in with your staff account',
    icon: ScanLine,
  },
];

export function AppChooserPage() {
  const { isAuthenticated, user } = useAuthStore();

  if (isAuthenticated && user?.role) return <Navigate to={homeForRole(user.role)} replace />;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[hsl(218_52%_13%)] px-4 py-10">
      <div className="w-full max-w-[420px]">
        <div className="mb-5 flex items-center gap-2.5">
          <Logo size={30} tone="reversed" className="flex-shrink-0" />
          <div>
            <p className="text-[15px] font-bold leading-tight text-white">SafePass</p>
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-white/45">
              Child pickup verification
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          {CHOICES.map(({ to, label, detail, action, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className={cn(
                'group flex items-start gap-3 rounded-lg border border-white/10 bg-card p-4',
                'transition-colors hover:border-primary/60 hover:bg-accent/40',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              )}
            >
              <span
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground transition-colors group-hover:border-primary/40 group-hover:text-primary"
                aria-hidden
              >
                <Icon size={17} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-bold text-foreground">{label}</span>
                <span className="mt-1 block text-[11px] leading-relaxed text-muted-foreground">
                  {detail}
                </span>
                <span className="mt-2 flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.06em] text-primary">
                  {action}
                  <ArrowRight size={11} className="transition-transform group-hover:translate-x-0.5" />
                </span>
              </span>
            </Link>
          ))}
        </div>

        {/* The admin dashboard is a different shape of product and a much smaller
            audience, so it is present but not competing with the two above. */}
        <p className="mt-4 text-center text-[11px] text-white/45">
          Administering a school?{' '}
          <Link
            to="/login"
            className="font-semibold text-white/80 underline decoration-white/30 underline-offset-2 transition-colors hover:text-white"
          >
            Open the school dashboard
          </Link>
        </p>
      </div>
    </div>
  );
}
